import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

interface ExecutiveSummaryCardProps {
  score: number;
  summary: string;
  compliancePassed: boolean;
  strengths: string[];
  criticalIssues: string[];
  recommendations: string[];
}

export default function ExecutiveSummaryCard({
  score,
  summary,
  compliancePassed,
  strengths,
  criticalIssues,
  recommendations,
}: ExecutiveSummaryCardProps) {
  const getScoreColor = (scoreValue: number) => {
    if (scoreValue >= 80) return 'success';
    if (scoreValue >= 60) return 'warning';
    return 'error';
  };

  const getScoreIcon = (scoreValue: number) => {
    if (scoreValue >= 80) return <CheckCircleIcon />;
    if (scoreValue >= 60) return <WarningIcon />;
    return <ErrorIcon />;
  };

  return (
    <Card elevation={3}>
      <CardContent>
        <Stack spacing={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" component="h2" fontWeight={600}>
              Review Summary
            </Typography>
            <Chip
              icon={getScoreIcon(score)}
              label={`${score}/100`}
              color={getScoreColor(score) as 'success' | 'warning' | 'error'}
              size="medium"
            />
          </Box>

          <Alert
            severity={compliancePassed ? 'success' : 'error'}
            icon={compliancePassed ? <CheckCircleIcon /> : <ErrorIcon />}
          >
            {compliancePassed ? '✓ Compliant' : '✗ Not Compliant'}
          </Alert>

          <Typography variant="body1">{summary}</Typography>

          <Divider />

          {/* Top Strengths (show first 3 in summary) */}
          {strengths.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ThumbUpIcon color="success" />
                  <Typography fontWeight={600}>Top Strengths ({strengths.length})</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {strengths.slice(0, 3).map((strength, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <CheckCircleIcon color="success" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={strength} />
                    </ListItem>
                  ))}
                  {strengths.length > 3 && (
                    <ListItem>
                      <ListItemText
                        primary={`+${strengths.length - 3} more...`}
                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Critical Issues (show first 3 in summary) */}
          {criticalIssues.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <WarningIcon color="error" />
                  <Typography fontWeight={600}>Critical Issues ({criticalIssues.length})</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {criticalIssues.slice(0, 3).map((issue, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <ErrorIcon color="error" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={issue} />
                    </ListItem>
                  ))}
                  {criticalIssues.length > 3 && (
                    <ListItem>
                      <ListItemText
                        primary={`+${criticalIssues.length - 3} more...`}
                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Recommendations (show first 3 in summary) */}
          {recommendations.length > 0 && (
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TipsAndUpdatesIcon color="primary" />
                  <Typography fontWeight={600}>Recommendations ({recommendations.length})</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <List dense>
                  {recommendations.slice(0, 3).map((rec, idx) => (
                    <ListItem key={idx}>
                      <ListItemIcon>
                        <TipsAndUpdatesIcon color="primary" fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={rec} />
                    </ListItem>
                  ))}
                  {recommendations.length > 3 && (
                    <ListItem>
                      <ListItemText
                        primary={`+${recommendations.length - 3} more...`}
                        sx={{ fontStyle: 'italic', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
